import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { opsApi, OpsApiError } from '../api/client';
import type { Client, CreateDailyLogBody, ProjectFileCategory, ProjectNoteCategory } from '../api/types';

/** A DELETE is idempotent: a 404 means the row is already gone, which is exactly
 *  the end-state the caller wanted. Swallow it so a double-click or a stale list
 *  converges silently instead of surfacing a scary "not found" error. */
async function deleteIdempotent(run: () => Promise<unknown>): Promise<void> {
  try {
    await run();
  } catch (e) {
    if (!(e instanceof OpsApiError && e.status === 404)) throw e;
  }
}

// ── Clients (contact store; create-project needs the list) ───────────────────
export function useClients(q?: string) {
  return useQuery({ queryKey: ['clients', q ?? ''], queryFn: () => opsApi.listClients(q) });
}
export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Client>) => opsApi.createClient(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}
export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: Partial<Client> }) =>
      opsApi.updateClient(vars.id, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}
export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => opsApi.deleteClient(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

// ── Projects ─────────────────────────────────────────────────────────────--
export function useProjects() {
  return useQuery({ queryKey: ['projects'], queryFn: opsApi.listProjects });
}
export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createProject(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}
export function useUpdateMilestone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: unknown }) =>
      opsApi.updateMilestone(vars.id, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

// ── Agent: activity log + kill-switch ────────────────────────────────────────
export function useAgentLog(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['agent-log', params],
    queryFn: () => opsApi.agentLog(params),
  });
}
export function useOverrideAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => opsApi.overrideAction(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-log'] }),
  });
}
export function useKillSwitch() {
  return useQuery({
    queryKey: ['kill-switch'],
    queryFn: opsApi.killSwitch,
    refetchInterval: 15_000,
  });
}
export function useSetKillSwitch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { halted: boolean; reason?: string }) =>
      opsApi.setKillSwitch(vars.halted, vars.reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kill-switch'] }),
  });
}

// ── Agent: scoped keys ───────────────────────────────────────────────────────
export function useAgentKeys() {
  return useQuery({ queryKey: ['agent-keys'], queryFn: opsApi.listAgentKeys });
}
export function useMintAgentKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; allowedTiers?: string[]; allowedTools?: string[] }) =>
      opsApi.mintAgentKey(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-keys'] }),
  });
}
export function useUpdateAgentKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      body: { allowedTiers?: string[]; allowedTools?: string[]; enabled?: boolean };
    }) => opsApi.updateAgentKey(vars.id, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-keys'] }),
  });
}
export function useRevokeAgentKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => opsApi.revokeAgentKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-keys'] }),
  });
}

// ── Agent: sessions + validation metrics + tool registry ─────────────────────
export function useAgentSessions() {
  return useQuery({
    queryKey: ['agent-sessions'],
    queryFn: () => opsApi.agentSessions(),
    refetchInterval: 15_000,
  });
}
export function useValidationMetrics() {
  return useQuery({
    queryKey: ['validation-metrics'],
    queryFn: opsApi.validationMetrics,
    refetchInterval: 30_000,
  });
}
export function useToolRegistry() {
  return useQuery({
    queryKey: ['tool-registry'],
    queryFn: opsApi.agentToolRegistry,
    staleTime: 5 * 60_000, // the contract changes rarely
  });
}

// ── Agent brain ──────────────────────────────────────────────────────────────
export function useBrainDocs() {
  return useQuery({ queryKey: ['brain', 'docs'], queryFn: opsApi.brainDocs });
}
export function useBrainRevisions(kind: string | null, slug: string | null) {
  return useQuery({
    queryKey: ['brain', 'revisions', kind, slug],
    queryFn: () => opsApi.brainRevisions(kind as string, slug as string),
    enabled: Boolean(kind && slug),
  });
}
export function useUpsertBrainDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import('../api/types').UpsertBrainDoc) => opsApi.brainUpsert(body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['brain'] });
      qc.invalidateQueries({ queryKey: ['brain', 'revisions', vars.kind, vars.slug] });
    },
  });
}
export function useRetireBrainDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { kind: string; slug: string }) => opsApi.brainRetire(vars.kind, vars.slug),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brain'] }),
  });
}
export function useRestoreBrainDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { kind: string; slug: string }) => opsApi.brainRestore(vars.kind, vars.slug),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brain'] }),
  });
}

// ── Team identity + RBAC (user management) ───────────────────────────────────
export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: opsApi.me, staleTime: 5 * 60_000 });
}
export function useTeam() {
  return useQuery({ queryKey: ['team'], queryFn: opsApi.listMembers });
}
export function useCreateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      username: string;
      password: string;
      name: string;
      email?: string;
      role: string;
      clientId?: string | null;
      telegramUserId?: string;
    }) => opsApi.createMember(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}
export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: Record<string, unknown> }) =>
      opsApi.updateMember(vars.id, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}
export function useResetMemberPassword() {
  return useMutation({
    mutationFn: (vars: { id: string; password: string }) =>
      opsApi.resetMemberPassword(vars.id, vars.password),
  });
}
export function useMemberPermissions(id?: string) {
  return useQuery({
    queryKey: ['member-permissions', id],
    queryFn: () => opsApi.memberPermissions(id as string),
    enabled: Boolean(id),
  });
}
export function useUpdateMemberPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; permissions: Record<string, boolean> | null }) =>
      opsApi.updateMemberPermissions(vars.id, vars.permissions),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['member-permissions', vars.id] });
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

// ── Project execution tracker ─────────────────────────────────────────────────

export function useProjectDashboard(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-dashboard', projectId],
    queryFn: () => opsApi.getProjectDashboard(projectId!),
    enabled: Boolean(projectId),
  });
}

export function useProjectShare(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-share', projectId],
    queryFn: () => opsApi.getProjectShare(projectId!),
    enabled: Boolean(projectId),
  });
}

export function useEnableProjectShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => opsApi.enableProjectShare(projectId),
    onSuccess: (_d, projectId) => {
      qc.invalidateQueries({ queryKey: ['project-share', projectId] });
    },
  });
}

export function useRevokeProjectShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => opsApi.revokeProjectShare(projectId),
    onSuccess: (_d, projectId) => {
      qc.invalidateQueries({ queryKey: ['project-share', projectId] });
    },
  });
}

// ── Project files (per-project document library) ──────────────────────────────

export function useProjectFiles(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-files', projectId],
    queryFn: () => opsApi.listProjectFiles(projectId!),
    enabled: Boolean(projectId),
  });
}

export function useUploadProjectFile(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      file: File;
      category?: ProjectFileCategory;
      name?: string;
      description?: string;
      visibleToClient?: boolean;
    }) => opsApi.uploadProjectFile(projectId, vars.file, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-files', projectId] }),
  });
}

export function useUpdateProjectFile(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      fileId: string;
      body: Partial<{ category: ProjectFileCategory; name: string; description: string | null; visibleToClient: boolean }>;
    }) => opsApi.updateProjectFile(vars.fileId, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-files', projectId] }),
  });
}

export function useReplaceProjectFile(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { fileId: string; file: File }) => opsApi.replaceProjectFile(vars.fileId, vars.file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-files', projectId] }),
  });
}

export function useDeleteProjectFile(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => opsApi.deleteProjectFile(fileId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-files', projectId] }),
  });
}

// ── Project notes (per-project "Daily Notes") ─────────────────────────────────

export function useProjectNotes(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-notes', projectId],
    queryFn: () => opsApi.listProjectNotes(projectId!),
    enabled: Boolean(projectId),
  });
}

export function useCreateProjectNote(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; category?: ProjectNoteCategory; body?: string; noteDate?: string }) =>
      opsApi.createProjectNote(projectId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-notes', projectId] }),
  });
}

export function useUpdateProjectNote(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      noteId: string;
      body: Partial<{ title: string; category: ProjectNoteCategory; body: string; noteDate: string }>;
    }) => opsApi.updateProjectNote(vars.noteId, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-notes', projectId] }),
  });
}

export function useDeleteProjectNote(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => opsApi.deleteProjectNote(noteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-notes', projectId] }),
  });
}

export function useDependencies(projectId: string | undefined) {
  return useQuery({
    queryKey: ['dependencies', projectId],
    queryFn: () => opsApi.listDependencies(projectId!),
    enabled: Boolean(projectId),
  });
}

/** Read-side schedule (stored scheduled_* + engine-computed critical path). */
export function useSchedule(projectId: string | undefined) {
  return useQuery({
    queryKey: ['schedule', projectId],
    queryFn: () => opsApi.getSchedule(projectId!),
    enabled: Boolean(projectId),
  });
}

/** Read-side per-project holiday list. */
export function useHolidays(projectId: string | undefined) {
  return useQuery({
    queryKey: ['holidays', projectId],
    queryFn: () => opsApi.listHolidays(projectId!),
    enabled: Boolean(projectId),
  });
}

/** Any plan/dependency/holiday edit recomputes scheduled_* + CP, so refresh the
 *  dashboard (item dates), the schedule (critical path), and the holiday list. */
function invalidateScheduleQueries(qc: ReturnType<typeof useQueryClient>, projectId: string) {
  qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] });
  qc.invalidateQueries({ queryKey: ['dependencies', projectId] });
  qc.invalidateQueries({ queryKey: ['schedule', projectId] });
  qc.invalidateQueries({ queryKey: ['holidays', projectId] });
  qc.invalidateQueries({ queryKey: ['milestone-links', projectId] });
}

/** Read-side polymorphic milestone↔node links (Gantt arrows + Plan-tab editor). */
export function useMilestoneLinks(projectId: string | undefined) {
  return useQuery({
    queryKey: ['milestone-links', projectId],
    queryFn: () => opsApi.listMilestoneLinks(projectId!),
    enabled: Boolean(projectId),
  });
}

export function useCreateMilestoneLink(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof opsApi.createMilestoneLink>[0]) =>
      opsApi.createMilestoneLink(body),
    onSuccess: () => invalidateScheduleQueries(qc, projectId),
  });
}

export function useUpdateMilestoneLinkLag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; lagDays: number }) =>
      opsApi.updateMilestoneLinkLag(vars.id, vars.lagDays),
    onSuccess: () => invalidateScheduleQueries(qc, projectId),
  });
}

export function useRemoveMilestoneLink(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteIdempotent(() => opsApi.removeMilestoneLink(id)),
    onSuccess: () => invalidateScheduleQueries(qc, projectId),
  });
}

export function useAddHoliday(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { holidayDate: string; label?: string | null }) =>
      opsApi.addHoliday(projectId, body),
    onSuccess: () => invalidateScheduleQueries(qc, projectId),
  });
}

export function useRemoveHoliday(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteIdempotent(() => opsApi.removeHoliday(id)),
    onSuccess: () => invalidateScheduleQueries(qc, projectId),
  });
}

export function useUpdateItemPlan(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { itemId: string; body: { plannedStart?: string | null; plannedEnd?: string | null } }) =>
      opsApi.updateItemPlan(vars.itemId, vars.body),
    onSuccess: () => invalidateScheduleQueries(qc, projectId),
  });
}

export function useAddDependency(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { predecessorId: string; successorId: string; lagDays?: number }) =>
      opsApi.addDependency(body),
    onSuccess: () => invalidateScheduleQueries(qc, projectId),
  });
}

export function useUpdateDependencyLag(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; lagDays: number }) =>
      opsApi.updateDependencyLag(vars.id, vars.lagDays),
    onSuccess: () => invalidateScheduleQueries(qc, projectId),
  });
}

export function useRemoveDependency(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteIdempotent(() => opsApi.removeDependency(id)),
    onSuccess: () => invalidateScheduleQueries(qc, projectId),
  });
}

export function useSetBaseline(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => opsApi.setBaseline(projectId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] }),
  });
}

// ── Project backups (snapshots) ──────────────────────────────────────────────
export function useProjectSnapshots(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-snapshots', projectId],
    queryFn: () => opsApi.listSnapshots(projectId!),
    enabled: Boolean(projectId),
  });
}

export function useCreateSnapshot(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name?: string) => opsApi.createSnapshot(projectId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-snapshots', projectId] }),
  });
}

export function useRestoreSnapshot(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapshotId: string) => opsApi.restoreSnapshot(projectId, snapshotId),
    // A restore rewrites the whole project — refetch everything that reads it.
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useDeleteSnapshot(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapshotId: string) => opsApi.deleteSnapshot(projectId, snapshotId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-snapshots', projectId] }),
  });
}

export function useImportSnapshot(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: FormData) => opsApi.importSnapshot(projectId, form),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-snapshots', projectId] }),
  });
}

export function useDailyLogs(trackId: string | undefined, params?: { from?: string; to?: string }) {
  return useQuery({
    queryKey: ['daily-logs', trackId, params],
    queryFn: () => opsApi.listDailyLogs(trackId!, params),
    enabled: Boolean(trackId),
  });
}

/** Edit a log entry (date, amount, note). Amount edits recompute the track %,
 *  so the dashboard and track queries refresh too. */
export function useUpdateDailyLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      logId: string;
      trackId: string;
      body: Partial<{ reportedDate: string; progressIncrement: number; note: string; hasProblem: boolean }>;
    }) => opsApi.updateDailyLog(vars.logId, vars.body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['daily-logs', vars.trackId] });
      qc.invalidateQueries({ queryKey: ['track', vars.trackId] });
      qc.invalidateQueries({ queryKey: ['project-dashboard'] });
    },
  });
}

/**
 * Progress photo as a blob object-URL fetched with the bearer token — the
 * /api/photos/* routes are auth-guarded and an <img src> can't carry an
 * Authorization header, so raw URLs 401 (blank images) in production.
 * Cached for the session: the photo bytes are immutable once uploaded.
 * Same pattern as the portal's usePortalPhotoSrc.
 */
export function useAuthedPhotoSrc(filePath: string): string | null {
  const { data } = useQuery({
    queryKey: ['photo-blob', filePath],
    queryFn: async () => URL.createObjectURL(await opsApi.photoBlob(filePath)),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data ?? null;
}

// ── Daily-log photos (W1) — every mutation invalidates the track's log list ──

export function useUploadLogPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { logId: string; trackId: string; file: File; caption?: string }) =>
      opsApi.uploadLogPhoto(vars.logId, vars.file, vars.caption),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['daily-logs', vars.trackId] }),
  });
}

export function useUpdatePhotoCaption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { photoId: string; trackId: string; caption: string | null }) =>
      opsApi.updatePhotoCaption(vars.photoId, vars.caption),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['daily-logs', vars.trackId] }),
  });
}

export function useDeleteLogPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { photoId: string; trackId: string }) => opsApi.deleteLogPhoto(vars.photoId),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['daily-logs', vars.trackId] }),
  });
}

export function useQualifications(projectId: string | undefined) {
  return useQuery({
    queryKey: ['qualifications', projectId],
    queryFn: () => opsApi.listQualifications(projectId!),
    enabled: Boolean(projectId),
  });
}

export function useChecklist(checklistId: string | undefined) {
  return useQuery({
    queryKey: ['checklist', checklistId],
    queryFn: () => opsApi.getChecklist(checklistId!),
    enabled: Boolean(checklistId),
  });
}

export function useProjectReport(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-report', projectId],
    queryFn: () => opsApi.getProjectReport(projectId!),
    enabled: Boolean(projectId),
  });
}

export function useCreateTrack(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createTrack(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] }),
  });
}

/** Track-level edits (W3 planned dates etc.) — dates feed the Gantt, so refresh schedule views too. */
export function useUpdateTrack(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: unknown }) => opsApi.updateTrack(vars.id, vars.body),
    onSuccess: () => invalidateScheduleQueries(qc, projectId),
  });
}

export function useAddTrackItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ trackId, body }: { trackId: string; body: unknown }) =>
      opsApi.addTrackItem(trackId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] }),
  });
}

/** Set a sub-task's % directly; the main task % rolls up from it, so refresh the dashboard. */
export function useSetItemProgress(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { itemId: string; progressPct: number }) =>
      opsApi.setItemProgress(vars.itemId, vars.progressPct),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] }),
  });
}

export function useDeleteTrack(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => opsApi.deleteTrack(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] }),
  });
}

export function useCreateMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createDeliveryMilestone(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] }),
  });
}

export function useDeleteMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteIdempotent(() => opsApi.deleteDeliveryMilestone(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] }),
  });
}

export function useCreateQualification(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createQualification(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] }),
  });
}

export function useAddChecklistItem(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.addChecklistItem(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] }),
  });
}

// ── Projects v2 workspace ─────────────────────────────────────────────────────

export function useUpdateProjectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; status: string }) =>
      opsApi.updateProjectStatus(vars.id, vars.status),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project-dashboard', vars.id] });
    },
  });
}

/** Web daily-log entry — same transactional path as the Telegram bot. */
export function useCreateDailyLog(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDailyLogBody) => opsApi.createDailyLog(body),
    onSuccess: (_d, body) => {
      qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] });
      qc.invalidateQueries({ queryKey: ['daily-logs', body.trackId] });
      qc.invalidateQueries({ queryKey: ['track', body.trackId] });
    },
  });
}

/** Completing a linked gate flips its delivery milestone status. */
export function useUpdateDeliveryMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: unknown }) =>
      opsApi.updateDeliveryMilestone(vars.id, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// ── Track-type catalog (Settings → Track Types) ───────────────────────────────

export function useTrackTypes(includeInactive = false) {
  return useQuery({
    queryKey: ['track-types', includeInactive],
    queryFn: () => opsApi.listTrackTypes(includeInactive),
  });
}

export function useCreateTrackType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { label: string; code?: string; defaultUnitLabel?: string; sortOrder?: number }) =>
      opsApi.createTrackType(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['track-types'] }),
  });
}

export function useUpdateTrackType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      body: Partial<{ label: string; defaultUnitLabel: string; sortOrder: number; active: boolean }>;
    }) => opsApi.updateTrackType(vars.id, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['track-types'] }),
  });
}

export function useReorderTrackTypes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: string[]) => opsApi.reorderTrackTypes(orderedIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['track-types'] }),
  });
}

// ── Finance epic: expenses ────────────────────────────────────────────────────
export function useExpenses(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['expenses', params],
    queryFn: () => opsApi.listExpenses(params),
  });
}
export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createExpense(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}
export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteIdempotent(() => opsApi.deleteExpense(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}

// ── Finance epic: statements + ledger posting ─────────────────────────────────
export function useStatements(period: string) {
  return useQuery({
    queryKey: ['statements', period],
    queryFn: async () => ({
      pnl: await opsApi.pnl(period),
      balanceSheet: await opsApi.balanceSheet(period),
      cashFlow: await opsApi.cashFlow(period),
    }),
    enabled: Boolean(period),
  });
}
export function usePostLedger() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => opsApi.postLedger(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['statements'] });
      qc.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
}

// ── Finance epic: budgets ─────────────────────────────────────────────────────
export function useBudgets() {
  return useQuery({ queryKey: ['budgets'], queryFn: opsApi.listBudgets });
}
export function useBudgetStatus(period?: string) {
  return useQuery({
    queryKey: ['budgets', 'status', period ?? 'all'],
    queryFn: () => opsApi.budgetStatus(period),
  });
}
export function useCreateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createBudget(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budgets'] }),
  });
}
export function useUpdateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: unknown }) => opsApi.updateBudget(vars.id, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budgets'] }),
  });
}
export function useDeleteBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteIdempotent(() => opsApi.deleteBudget(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budgets'] }),
  });
}

// ── Finance epic: quotations ──────────────────────────────────────────────────
export function useQuotations() {
  return useQuery({ queryKey: ['quotations'], queryFn: opsApi.listQuotations });
}
export function useCreateQuotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createQuotation(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotations'] }),
  });
}
export function useQuotationAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; action: 'send' | 'accept' | 'convert'; body?: unknown }) =>
      vars.action === 'send'
        ? opsApi.sendQuotation(vars.id)
        : vars.action === 'accept'
          ? opsApi.acceptQuotation(vars.id)
          : opsApi.convertQuotation(vars.id, vars.body ?? {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotations'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// ── Finance epic: invoices ────────────────────────────────────────────────────
export function useInvoices() {
  return useQuery({ queryKey: ['invoices'], queryFn: opsApi.listInvoices });
}
export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createInvoice(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['projects'] }); // milestones get claimed
    },
  });
}
export function useInvoiceAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; action: 'issue' | 'pay'; body?: unknown }) =>
      vars.action === 'issue'
        ? opsApi.issueInvoice(vars.id)
        : opsApi.recordPayment(vars.id, vars.body ?? {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['projects'] }); // milestone statuses flip
      qc.invalidateQueries({ queryKey: ['statements'] });
    },
  });
}

// ── Finance epic: approval inbox ──────────────────────────────────────────────
export function useApprovals(status?: string) {
  return useQuery({
    queryKey: ['approvals', status ?? 'all'],
    queryFn: () => opsApi.listApprovals(status),
    refetchInterval: 15_000, // the inbox is the most-watched screen; keep it live
  });
}
export function useApprovalDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; action: 'approve' | 'reject'; note?: string }) =>
      vars.action === 'approve'
        ? opsApi.approveRequest(vars.id, vars.note)
        : opsApi.rejectRequest(vars.id, vars.note ?? ''),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvals'] });
      qc.invalidateQueries({ queryKey: ['agent-log'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['quotations'] });
    },
  });
}

// ── Finance epic: tax-prep ────────────────────────────────────────────────────
export function usePpnWorksheet(period: string) {
  return useQuery({
    queryKey: ['ppn', period],
    queryFn: () => opsApi.ppnWorksheet(period),
    enabled: /^\d{4}-\d{2}$/.test(period),
  });
}
export function usePphBadan(year: number) {
  return useQuery({ queryKey: ['pph-badan', year], queryFn: () => opsApi.pphBadan(year) });
}
export function useFinalPphWindow() {
  return useQuery({ queryKey: ['final-pph-window'], queryFn: opsApi.finalPphWindow });
}
export function useSetFinalPphWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (startYear: string) => opsApi.setFinalPphWindow(startYear),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['final-pph-window'] });
      qc.invalidateQueries({ queryKey: ['pph-badan'] });
      qc.invalidateQueries({ queryKey: ['compliance-calendar'] });
    },
  });
}
export function useTaxPreps(period?: string) {
  return useQuery({
    queryKey: ['tax-preps', period ?? 'all'],
    queryFn: () => opsApi.listTaxPreps(period),
  });
}
export function useGenerateTaxPrep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taxType: import('../api/types').TaxType; period: string }) =>
      opsApi.generateTaxPrep(vars.taxType, vars.period),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tax-preps'] }),
  });
}

// ── Finance epic: compliance ──────────────────────────────────────────────────
export function useComplianceCalendar(year: number) {
  return useQuery({
    queryKey: ['compliance-calendar', year],
    queryFn: () => opsApi.complianceCalendar(year),
  });
}
export function useCompliancePackages() {
  return useQuery({ queryKey: ['compliance', 'packages'], queryFn: opsApi.compliancePackages });
}
export function useAssemblePackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (period: string) => opsApi.assemblePackage(period),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance', 'packages'] }),
  });
}
export function usePackageDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; action: 'approve' | 'handoff'; handedOffTo?: string }) =>
      vars.action === 'approve'
        ? opsApi.approvePackage(vars.id)
        : opsApi.handoffPackage(vars.id, vars.handedOffTo ?? 'konsultan pajak'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance', 'packages'] }),
  });
}

// ── Finance epic: petty cash ──────────────────────────────────────────────────
export function usePettyCashTopups() {
  return useQuery({ queryKey: ['pettyCash', 'topups'], queryFn: opsApi.pettyCashTopups });
}
export function usePettyCashBalance() {
  return useQuery({ queryKey: ['pettyCash', 'balance'], queryFn: opsApi.pettyCashBalance });
}
export function usePettyCashSettlement(from: string | null, to: string | null) {
  return useQuery({
    queryKey: ['pettyCash', 'settlement', from, to],
    queryFn: () => opsApi.pettyCashSettlement(from as string, to as string),
    enabled: Boolean(from && to),
  });
}
export function useCreatePettyCashTopup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      amount: number;
      custodian: string;
      transferRef?: string;
      toppedUpAt: string;
      notes?: string;
    }) => opsApi.createPettyCashTopup(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pettyCash'] }),
  });
}
export function useCreatePettyCashVoucher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { fields: Record<string, string | number>; receipt: File }) =>
      opsApi.createPettyCashVoucher(vars.fields, vars.receipt),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pettyCash'] });
      qc.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}
export function usePettyCashReconcile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { countedAmount: number; countedAt?: string; notes?: string }) =>
      opsApi.pettyCashReconcile(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pettyCash'] }),
  });
}

// ── Finance epic: tax close (DPB / registers / PPh 21) ────────────────────────
export function usePeredaranBruto(year: number) {
  return useQuery({
    queryKey: ['taxClose', 'dpb', year],
    queryFn: () => opsApi.peredaranBruto(year),
  });
}
export function useBupotReceived(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['taxClose', 'bupotReceived', params],
    queryFn: () => opsApi.listBupotReceived(params),
  });
}
export function useBupotIssued(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['taxClose', 'bupotIssued', params],
    queryFn: () => opsApi.listBupotIssued(params),
  });
}
export function useTaxPayments(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['taxClose', 'payments', params],
    queryFn: () => opsApi.listTaxPayments(params),
  });
}
export function useCreateBupotReceived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createBupotReceived(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taxClose'] }),
  });
}
export function useUpdateBupotIssued() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: unknown }) =>
      opsApi.updateBupotIssued(vars.id, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taxClose'] }),
  });
}
export function useCreateTaxPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createTaxPayment(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taxClose'] }),
  });
}
export function useMarkTaxPaymentPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; paidAt: string; ntpn: string }) =>
      opsApi.markTaxPaymentPaid(vars.id, { paidAt: vars.paidAt, ntpn: vars.ntpn }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['taxClose'] });
      qc.invalidateQueries({ queryKey: ['compliance'] });
    },
  });
}
export function useSalaryRecords(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['taxClose', 'salary', params],
    queryFn: () => opsApi.listSalaryRecords(params),
  });
}
export function useCreateSalaryRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createSalaryRecord(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taxClose'] }),
  });
}
export function useMarkSalaryPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; paidAt: string }) =>
      opsApi.markSalaryPaid(vars.id, { paidAt: vars.paidAt }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taxClose'] }),
  });
}

// ── Finance epic: vendors + withheld payments ─────────────────────────────────
export function useVendors() {
  return useQuery({ queryKey: ['vendors'], queryFn: opsApi.listVendors });
}
export function useCreateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createVendor(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  });
}
export function useVendorPayments(status?: string) {
  return useQuery({
    queryKey: ['vendorPayments', status ?? 'all'],
    queryFn: () => opsApi.listVendorPayments(status),
  });
}
export function useCreateVendorPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => opsApi.createVendorPayment(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendorPayments'] }),
  });
}
export function useVendorPaymentAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; action: 'approve' | 'mark-paid'; paidAt?: string }) =>
      vars.action === 'approve'
        ? opsApi.approveVendorPayment(vars.id)
        : opsApi.markVendorPaymentPaid(vars.id, { paidAt: vars.paidAt ?? '' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendorPayments'] });
      qc.invalidateQueries({ queryKey: ['taxClose'] });
      qc.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}

// ── HR: people (endpoints land in P4; the PPh 21 tab reads the list) ─────────
export function usePeople(params: Record<string, unknown> = {}) {
  return useQuery({ queryKey: ['people', params], queryFn: () => opsApi.listPeople(params) });
}

// ── HR (P4): people mutations / contracts / leave ────────────────────────────
export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => opsApi.createPerson(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['people'] }),
  });
}
export function useUpdatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: Record<string, unknown> }) =>
      opsApi.updatePerson(vars.id, vars.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['people'] });
      qc.invalidateQueries({ queryKey: ['contracts'] });
    },
  });
}
export function useDeactivatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => opsApi.deactivatePerson(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['people'] }),
  });
}
export function useExpiringContracts() {
  return useQuery({ queryKey: ['contracts', 'expiring'], queryFn: opsApi.expiringContracts });
}
export function useContracts(personId: string | null) {
  return useQuery({
    queryKey: ['contracts', personId],
    queryFn: () => opsApi.listContracts(personId as string),
    enabled: !!personId,
  });
}
export function useAddContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { personId: string; body: Record<string, unknown> }) =>
      opsApi.addContract(vars.personId, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  });
}

export function useLeave(params: Record<string, unknown> = {}) {
  return useQuery({ queryKey: ['leave', params], queryFn: () => opsApi.listLeave(params) });
}
export function useCreateLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => opsApi.createLeave(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave'] });
      qc.invalidateQueries({ queryKey: ['leaveBalances'] });
    },
  });
}
export function useDecideLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      decision: 'approved' | 'rejected';
      note?: string;
      allowOverbalance?: boolean;
    }) => opsApi.decideLeave(vars.id, vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leave'] });
      qc.invalidateQueries({ queryKey: ['leaveBalances'] });
    },
  });
}
export function useLeaveBalances(year?: number) {
  return useQuery({
    queryKey: ['leaveBalances', year ?? 'current'],
    queryFn: () => opsApi.leaveBalances(year),
  });
}

// ── HR (P4): payroll export ───────────────────────────────────────────────────
export function usePayrollExports() {
  return useQuery({ queryKey: ['payroll'], queryFn: opsApi.listPayrollExports });
}
export function usePayrollPreview() {
  return useQuery({ queryKey: ['payroll', 'preview'], queryFn: opsApi.payrollPreview });
}
export function useGeneratePayrollExport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { period: string; provider?: 'talenta' | 'manual' }) =>
      opsApi.generatePayrollExport(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
}

// ── Finance epic: labor allocation + project P&L (P5) ────────────────────────
export function useProjectPnl(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-pnl', projectId],
    queryFn: () => opsApi.projectPnl(projectId as string),
    enabled: Boolean(projectId),
  });
}
export function useLaborAllocations(month: string) {
  return useQuery({
    queryKey: ['labor-allocations', month],
    queryFn: () => opsApi.laborAllocations(month),
    enabled: /^\d{4}-\d{2}$/.test(month),
  });
}
export function useCloseLaborMonth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (month: string) => opsApi.closeLaborMonth(month),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['labor-allocations'] });
      qc.invalidateQueries({ queryKey: ['project-pnl'] });
    },
  });
}
